import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  EmptyState,
  PageHeader,
  SeverityBadge,
  Spinner,
} from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { getOrgFindings } from '../lib/api';

/**
 * /o/:orgSlug/findings — Org-tier cross-workspace findings list.
 *
 * V3-8-6: Aggregates findings across all child workspaces of the
 * organization. Auth: organizationContext() on the server side.
 */

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function mapSeverity(s: string): 'critical' | 'high' | 'elevated' | 'moderate' | 'standard' {
  const map: Record<string, 'critical' | 'high' | 'elevated' | 'moderate' | 'standard'> = {
    critical: 'critical',
    high: 'high',
    medium: 'moderate',
    low: 'standard',
  };
  return map[s] ?? 'standard';
}

function statusColor(s: string): string {
  switch (s) {
    case 'open':
      return 'var(--status-red)';
    case 'acknowledged':
      return 'var(--status-amber)';
    case 'remediated':
    case 'auto_closed':
    case 'closed':
      return 'var(--success)';
    default:
      return 'var(--text-muted)';
  }
}

function statusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function isOverdue(deadline: string): boolean {
  try {
    return new Date(deadline).getTime() < Date.now();
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function OrgFindings() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();

  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const dataQuery = useQuery({
    queryKey: queryKeys.orgFindings(orgSlug ?? ''),
    queryFn: () => getOrgFindings(orgSlug!),
    enabled: !!orgSlug,
  });
  const data = {
    data: dataQuery.data ?? null,
    error: dataQuery.error?.message ?? null,
    loading: dataQuery.isLoading,
  };

  const filtered = useMemo(() => {
    if (!data.data?.findings) return [];
    let items = data.data.findings;
    if (severityFilter !== 'all') {
      items = items.filter((f) => f.severity === severityFilter);
    }
    if (statusFilter !== 'all') {
      items = items.filter((f) => f.status === statusFilter);
    }
    return items;
  }, [data.data, severityFilter, statusFilter]);

  if (data.loading) return <Spinner label="Loading findings" />;
  if (data.error) return <Alert variant="error">{data.error}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Findings"
        subtitle={`${data.data?.total ?? 0} finding${(data.data?.total ?? 0) === 1 ? '' : 's'} across all workspaces`}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
          }}
        >
          Severity
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            style={{
              fontSize: 'var(--font-size-2xs)',
              padding: '4px 8px',
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="all">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
          }}
        >
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              fontSize: 'var(--font-size-2xs)',
              padding: '4px 8px',
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="remediated">Remediated</option>
            <option value="auto_closed">Auto-closed</option>
            <option value="closed">Closed</option>
          </select>
        </label>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState
          title="No findings"
          description="No open findings across your workspaces."
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-2xs)',
              color: 'var(--text)',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={thStyle}>Severity</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Control</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Status</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Workspace</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>SLA Due</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Opened</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => {
                const overdue =
                  f.sla_breached ||
                  (['open', 'acknowledged'].includes(f.status) &&
                    isOverdue(f.sla_deadline_at));
                return (
                  <tr
                    key={f.finding_id}
                    onClick={() =>
                      f.workspace_slug &&
                      navigate(`/w/${f.workspace_slug}/app/dashboard`)
                    }
                    style={{
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                    }}
                  >
                    <td style={tdStyle}>
                      <SeverityBadge severity={mapSeverity(f.severity)} />
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 500,
                      }}
                    >
                      {f.control_id}
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          color: statusColor(f.status),
                          fontWeight: 500,
                          fontSize: 'var(--font-size-3xs)',
                        }}
                      >
                        {statusLabel(f.status)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <Badge tone="neutral">{f.workspace_name || f.workspace_slug}</Badge>
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color: overdue ? 'var(--status-red)' : 'var(--text-muted)',
                        fontWeight: overdue ? 600 : 400,
                      }}
                    >
                      {formatDate(f.sla_deadline_at)}
                      {overdue ? ' (overdue)' : ''}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>
                      {formatDate(f.opened_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared cell styles                                                 */
/* ------------------------------------------------------------------ */

const thStyle: React.CSSProperties = {
  padding: 'var(--space-2)',
  color: 'var(--text-muted)',
  fontWeight: 500,
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--space-2)',
  whiteSpace: 'nowrap',
};
